/**
 * <scratch-popup> — popup jeu à gratter (sections/section-scratch-popup.liquid)
 *
 * Étapes : scratch → email → unlocked
 * État persistant (localStorage 'siliane-scratch') :
 *   { prize: index, snoozeUntil: ts, claimed: bool }
 * - prize figé au 1er affichage (pas de re-roll en rechargeant)
 * - claimed = email soumis → plus jamais d'ouverture auto
 * - la popup ne quitte jamais la page ; le code n'est appliqué qu'au clic sur « Copier et appliquer »
 * - formulaire HTML pur (pas de {% form 'customer' %}) : le hCaptcha Shopify rechargerait la page
 */
(() => {
  if (customElements.get('scratch-popup')) return;

  const STORAGE_KEY = 'siliane-scratch';

  const readState = () => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch (e) {
      return {};
    }
  };
  const writeState = (patch) => {
    const next = { ...readState(), ...patch };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (e) {
      /* navigation privée : pas de mémorisation, tant pis */
    }
    return next;
  };

  class ScratchPopup extends HTMLElement {
    /** Compteur anonyme (affichée / grattée / opt-in / fermée) → dashboard du relais. */
    track(event, extra = {}) {
      if (!this.dataset.relayUrl || this.designMode) return;
      const body = JSON.stringify({ event, page: this.dataset.page || 'other', prize: this.code, ...extra });
      try {
        // text/plain = requête "simple", pas de preflight CORS, keepalive survit à la navigation
        fetch(`${this.dataset.relayUrl.replace(/\/$/, '')}/event`, {
          method: 'POST',
          body,
          keepalive: true,
          headers: { 'Content-Type': 'text/plain' },
        }).catch(() => {});
      } catch (e) {
        /* jamais bloquant */
      }
    }

    connectedCallback() {
      this.dialog = this.querySelector('.siliane-scratch__dialog');
      this.canvas = this.querySelector('[data-scratch-canvas]');
      this.card = this.querySelector('[data-scratch-card]');
      this.form = this.querySelector('.siliane-scratch__form');
      this.teaser = document.querySelector('[data-scratch-teaser]');
      this.prizes = [...this.querySelectorAll('[data-prize]')];
      this.threshold = Number(this.dataset.threshold || 45) / 100;
      this.designMode = this.dataset.designMode === 'true';
      this.revealed = false;
      this.state = readState();

      this.querySelectorAll('[data-scratch-close]').forEach((el) =>
        el.addEventListener('click', () => this.close())
      );
      this.querySelector('[data-scratch-reveal]').addEventListener('click', () => this.reveal());
      this.querySelector('[data-scratch-apply]').addEventListener('click', () => this.apply());
      this.form.addEventListener('submit', (e) => this.submit(e));
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
      if (this.teaser) this.teaser.addEventListener('click', () => this.open());

      if (this.designMode) {
        this.bindEditor();
        return;
      }

      if (this.state.claimed) return;

      if (this.state.snoozeUntil && Date.now() < this.state.snoozeUntil) {
        this.showTeaser();
        return;
      }
      this.scheduleOpen();
    }

    /* ---------- Déclenchement ---------- */

    scheduleOpen() {
      const delay = Number(this.dataset.delay || 0) * 1000;
      this.timer = setTimeout(() => this.open(), delay);

      if (this.dataset.exitIntent === 'true' && window.matchMedia('(pointer: fine)').matches) {
        this.onExit = (e) => {
          if (e.clientY <= 0 && !e.relatedTarget) this.open();
        };
        document.addEventListener('mouseout', this.onExit);
      }
    }

    clearTriggers() {
      clearTimeout(this.timer);
      if (this.onExit) document.removeEventListener('mouseout', this.onExit);
    }

    bindEditor() {
      document.addEventListener('shopify:section:select', (e) => {
        if (this.closest('.shopify-section')?.id.endsWith(e.detail.sectionId)) this.open({ reset: true });
      });
      document.addEventListener('shopify:section:deselect', (e) => {
        if (this.closest('.shopify-section')?.id.endsWith(e.detail.sectionId)) this.close({ silent: true });
      });
      document.addEventListener('shopify:block:select', (e) => {
        const index = this.prizes.findIndex((p) => p.hasAttribute('data-shopify-editor-block') && p.dataset.shopifyEditorBlock.includes(e.detail.blockId));
        if (index > -1) this.open({ reset: true, prize: index });
      });
    }

    /* ---------- Ouverture / fermeture ---------- */

    open({ unlocked = false, reset = false, prize } = {}) {
      if (this.isOpen && !reset) return;
      this.clearTriggers();
      this.isOpen = true;
      this.lastFocus = document.activeElement;

      this.selectPrize(prize);
      this.hidden = false;
      if (this.teaser) this.teaser.hidden = true;
      document.documentElement.classList.add('siliane-scratch-lock');

      if (!this.shownTracked && !unlocked) {
        this.shownTracked = true;
        this.track('shown');
      }

      if (unlocked) {
        this.reveal({ silent: true });
        this.goTo('unlocked');
      } else if (reset || !this.revealed) {
        this.revealed = false;
        this.classList.remove('is-revealed');
        this.goTo('scratch');
        requestAnimationFrame(() => this.paintFoil());
      }
      this.dialog.focus();
    }

    close({ silent = false } = {}) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.hidden = true;
      document.documentElement.classList.remove('siliane-scratch-lock');
      if (silent || this.designMode) return;

      if (!this.revealed) this.track('closed');
      if (!readState().claimed) {
        const days = Number(this.dataset.snoozeDays || 3);
        this.state = writeState({ snoozeUntil: Date.now() + days * 864e5 });
        this.showTeaser();
      }
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    }

    showTeaser() {
      if (this.teaser && !readState().claimed) this.teaser.hidden = false;
    }

    /* ---------- Lot ---------- */

    selectPrize(forced) {
      let index = forced;
      if (index === undefined) {
        index = this.state.prize;
        if (index === undefined || !this.prizes[index]) {
          index = this.drawPrize();
          if (!this.designMode) this.state = writeState({ prize: index });
        }
      }
      this.prizes.forEach((p, i) => (p.hidden = i !== index));
      this.prize = this.prizes[index];
      this.code = this.prize.dataset.code;
      this.querySelector('[data-scratch-code]').textContent = this.code;
    }

    drawPrize() {
      const weights = this.prizes.map((p) => Math.max(Number(p.dataset.weight) || 1, 1));
      let roll = Math.random() * weights.reduce((a, b) => a + b, 0);
      for (let i = 0; i < weights.length; i++) {
        roll -= weights[i];
        if (roll < 0) return i;
      }
      return 0;
    }

    /* ---------- Carte à gratter ---------- */

    paintFoil() {
      const rect = this.card.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const canvas = this.canvas;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.globalCompositeOperation = 'source-over';

      const styles = getComputedStyle(this);
      const a = styles.getPropertyValue('--scratch-foil-a').trim() || '#B8955A';
      const b = styles.getPropertyValue('--scratch-foil-b').trim() || '#E6D3A3';
      const w = rect.width;
      const h = rect.height;

      // Métal brossé : dégradé diagonal + reflets + grain
      const grad = ctx.createLinearGradient(0, 0, w, h);
      grad.addColorStop(0, a);
      grad.addColorStop(0.35, b);
      grad.addColorStop(0.55, a);
      grad.addColorStop(0.8, b);
      grad.addColorStop(1, a);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      ctx.globalAlpha = 0.08;
      for (let y = 0; y < h; y += 2) {
        ctx.fillStyle = Math.random() > 0.5 ? '#fff' : '#000';
        ctx.fillRect(0, y, w, 1);
      }
      ctx.globalAlpha = 1;

      // Hachures "à gratter"
      ctx.save();
      ctx.globalAlpha = 0.14;
      ctx.strokeStyle = '#000';
      ctx.lineWidth = w * 0.045;
      ctx.lineCap = 'round';
      for (let i = 0; i < 5; i++) {
        const x = w * (0.28 + i * 0.1);
        ctx.beginPath();
        ctx.moveTo(x, h * 0.3);
        ctx.lineTo(x + w * 0.07, h * 0.72);
        ctx.stroke();
      }
      ctx.restore();

      const label = canvas.dataset.label;
      if (label) {
        ctx.font = `700 ${Math.round(w * 0.075)}px ${styles.getPropertyValue('--font-primary--family') || 'sans-serif'}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        ctx.fillText(label, w / 2, h / 2);
      }

      this.ctx = ctx;
      this.brush = Math.max(w * 0.09, 24);
      this.bindScratch();
    }

    bindScratch() {
      if (this.scratchBound) return;
      this.scratchBound = true;
      let drawing = false;
      let last = null;
      let moves = 0;

      const point = (e) => {
        const r = this.canvas.getBoundingClientRect();
        return { x: e.clientX - r.left, y: e.clientY - r.top };
      };

      this.canvas.addEventListener('pointerdown', (e) => {
        if (this.revealed) return;
        drawing = true;
        last = point(e);
        this.canvas.setPointerCapture(e.pointerId);
        this.scratch(last, last);
      });
      this.canvas.addEventListener('pointermove', (e) => {
        if (!drawing || this.revealed) return;
        const p = point(e);
        this.scratch(last, p);
        last = p;
        if (++moves % 8 === 0) this.checkProgress();
      });
      const stop = () => {
        if (!drawing) return;
        drawing = false;
        this.checkProgress();
      };
      this.canvas.addEventListener('pointerup', stop);
      this.canvas.addEventListener('pointercancel', stop);
    }

    scratch(from, to) {
      const ctx = this.ctx;
      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineWidth = this.brush;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(from.x, from.y);
      ctx.lineTo(to.x + 0.1, to.y + 0.1);
      ctx.stroke();
    }

    checkProgress() {
      const { width, height } = this.canvas;
      const data = this.ctx.getImageData(0, 0, width, height).data;
      const stride = 32; // échantillonne 1 pixel sur 8 (4 canaux × 8)
      let cleared = 0;
      let total = 0;
      for (let i = 3; i < data.length; i += stride) {
        total++;
        if (data[i] === 0) cleared++;
      }
      if (cleared / total >= this.threshold) this.reveal();
    }

    reveal({ silent = false } = {}) {
      if (this.revealed) return;
      this.revealed = true;
      this.classList.add('is-revealed');
      if (!silent) {
        this.track('revealed');
        if (navigator.vibrate) navigator.vibrate(40);
        this.goTo('email');
        setTimeout(() => this.querySelector('.siliane-scratch__input')?.focus({ preventScroll: true }), 350);
      }
    }

    /* ---------- Étapes ---------- */

    goTo(step) {
      this.classList.toggle('is-unlocked', step === 'unlocked');
      this.querySelectorAll('[data-step]').forEach((el) => (el.hidden = el.dataset.step !== step));
      this.querySelectorAll('[data-step-text]').forEach((el) => (el.hidden = el.dataset.stepText !== step));
    }

    /* ---------- Soumission email ---------- */

    async submit(e) {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (this.designMode) {
        this.goTo('unlocked');
        return;
      }
      const button = this.form.querySelector('[type="submit"]');
      const error = this.form.querySelector('[data-scratch-error]');
      button.disabled = true;
      error.hidden = true;

      const email = this.form.querySelector('[name="email"]').value.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        error.textContent = 'Adresse email invalide.';
        error.hidden = false;
        button.disabled = false;
        return;
      }
      try {
        window.omnisend?.identifyContact?.({ email });
      } catch (err) {
        /* snippet Omnisend absent : le relais / la synchro Shopify suffit */
      }

      // Relais Cloudflare → API Omnisend (email + téléphone + code, consentement horodaté)
      if (this.dataset.relayUrl) {
        const phone = this.form.querySelector('[data-scratch-phone]')?.value.trim() || '';
        const phoneConsent = !!this.form.querySelector('[data-scratch-phone-consent]')?.checked;
        const honeypot = this.form.querySelector('[data-scratch-honeypot]')?.value || '';
        try {
          const res = await fetch(this.dataset.relayUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email,
              phone,
              phoneConsent,
              code: this.code,
              prize: this.prize.querySelector('.siliane-scratch__prize-label')?.textContent.trim(),
              website: honeypot,
            }),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            error.textContent =
              data.error === 'phone'
                ? 'Numéro de téléphone invalide.'
                : data.error === 'email'
                  ? 'Adresse email invalide.'
                  : 'Un problème est survenu, réessayez dans un instant.';
            error.hidden = false;
            button.disabled = false;
            return;
          }
        } catch (err) {
          error.textContent = 'Connexion impossible, réessayez dans un instant.';
          error.hidden = false;
          button.disabled = false;
          return;
        }
        this.track('optin', { phone: !!phone });
        this.unlock();
        button.disabled = false;
        return;
      }

      // Pas de relais configuré : on débloque sans enregistrer le contact
      this.unlock();
      button.disabled = false;
    }

    /* Code débloqué : mémorise et affiche le code (application au clic uniquement) */
    unlock() {
      this.state = writeState({ claimed: true });
      this.goTo('unlocked');
    }

    /* ---------- Code ---------- */

    /** Un seul bouton : copie le code et le pose sur le panier Shopify. */
    async apply() {
      const button = this.querySelector('[data-scratch-apply]');
      const status = this.querySelector('[data-scratch-applied]');
      if (this.applied) {
        const url = this.dataset.ctaUrl;
        return url ? (window.location.href = url) : this.close({ silent: true });
      }
      button.disabled = true;
      try {
        await navigator.clipboard.writeText(this.code);
      } catch (e) {
        /* copie facultative */
      }
      let ok = false;
      try {
        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: this.code }),
        });
        const cart = await res.json();
        const entry = (cart.discount_codes || []).find((d) => d.code.toUpperCase() === this.code.toUpperCase());
        ok = res.ok && (!cart.item_count || entry?.applicable);
        fetch(`/discount/${encodeURIComponent(this.code)}`, { credentials: 'same-origin' }).catch(() => {});
        document.querySelector('cart-drawer')?.refresh?.();
      } catch (e) {
        /* le code reste copié, saisie manuelle possible au checkout */
      }
      button.disabled = false;
      status.textContent = ok ? 'Code copié et appliqué à votre panier' : 'Code copié, à saisir au paiement';
      status.hidden = false;
      if (ok) {
        this.applied = true;
        button.textContent = 'Continuer mes achats';
      }
    }
  }

  customElements.define('scratch-popup', ScratchPopup);
})();
